import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Dices,
  ExternalLink,
  Link2,
  Plus,
  Search,
  ShoppingBag,
  Trash2,
  UserPlus,
} from "lucide-react";
import {
  createInvite,
  deleteInvite,
  generateInviteSuffix,
  generateProductLink,
  getShopModeLink,
  listMyInvites,
  searchProducts,
  type AgentInvite,
  type WCProduct,
  type WCProductSearchResponse,
} from "../lib/agent";
import { Button, Card, Input } from "../components/ui";
import { cn } from "../lib/cn";
import { AdminPreviewBanner, useIsAgent as isAgent } from "../components/AdminPreviewBanner";
import { ShareButtons } from "../components/ShareButtons";
import { FieldHelp, HelpBox, InfoTip } from "../components/InfoTip";
import { StockBadge } from "../components/StockBadge";

export function AgentLinks() {
  const userIsAgent = isAgent();
  return (
    <main className="mx-auto w-full max-w-6xl gradient-radial-warm px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Empfehlungs-Links</h2>
        <p className="mt-2 text-sm text-ink-muted">
          Deine Tracking-Codes + persönliche Links zu bestimmten Produkten.
        </p>
      </div>

      {!userIsAgent && <AdminPreviewBanner pageName="Empfehlungs-Link" />}

      {userIsAgent && (
        <div className="mb-6">
          <HelpBox kind="tip" collapsible title="🔗 Welcher Link wofür?">
            <strong>3 Arten von Links</strong> — alle tracken Verkäufe auf dein
            Konto:
            <br />
            <br />
            <strong>1. Shop-Modus</strong> — du surfst direkt auf dpconnect.de
            mit eingeblendetem Teilen-Button. Schnellste Option wenn du
            spontan was empfehlen willst.
            <br />
            <br />
            <strong>2. Produkt-Link</strong> — direkter Link zu einem
            bestimmten Produkt. Perfekt für gezielte Empfehlungen („Schau dir
            diese Snackbox an: <em>link</em>").
            <br />
            <br />
            <strong>3. Empfehlungs-Codes</strong> — generelle
            Tracking-Codes, z.B. für Kampagnen (Insta, Newsletter, QR auf
            Flyer). Pro Kampagne ein eigener Code → du siehst was wirklich
            funktioniert.
          </HelpBox>
        </div>
      )}

      <RegistrationLinkCard />
      {userIsAgent && <ShopModeCard />}
      <ProductLinkGenerator />
      <InvitesSection />
    </main>
  );
}

// ============================================================================
// Registrierungs-Link für Neukunden — vorgebauter Link auf /kunde-werden/
// ============================================================================

function RegistrationLinkCard() {
  const { data: invites } = useQuery<AgentInvite[]>({
    queryKey: ["agent-invites"],
    queryFn: () => listMyInvites(),
  });
  const [copiedCode, setCopiedCode] = useState(false);

  const def =
    invites?.find((i) => i.is_default && i.is_active) ||
    invites?.find((i) => i.is_active);
  if (!def) return null;

  // Registrierungs-Link aus dem ref_link ableiten — so bleibt der
  // Parameter-Name (?guthaben=…) automatisch synchron mit den Settings.
  let regLink = "https://dpconnect.de/kunde-werden/";
  let paramName = "guthaben";
  try {
    const u = new URL(def.ref_link);
    regLink = `https://dpconnect.de/kunde-werden/${u.search}`;
    paramName = [...u.searchParams.keys()][0] || "guthaben";
  } catch {
    regLink = `https://dpconnect.de/kunde-werden/?guthaben=${def.invite_code}`;
  }

  function copyCode() {
    navigator.clipboard.writeText(def!.invite_code).then(() => {
      setCopiedCode(true);
      window.setTimeout(() => setCopiedCode(false), 1500);
    });
  }

  return (
    <Card className="mb-6 border-emerald-200 bg-emerald-50/40">
      <div className="flex items-start gap-3 mb-3">
        <UserPlus className="size-4 text-emerald-700 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            Registrierungs-Link für Neukunden
            <InfoTip title="Was passiert mit dem Link?">
              Führt direkt zur Kunden-Registrierung auf dpconnect.de. Dein
              Code steckt im Link — jeder, der sich darüber registriert, wird
              dauerhaft dir zugeordnet und du verdienst an seinen Bestellungen.
            </InfoTip>
          </h3>
          <p className="text-xs text-emerald-800 font-medium mt-0.5">
            🎁 Neukunden erhalten 100 € Guthaben für ihre Registrierung — sag
            es beim Teilen dazu!
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-white border border-line px-3 py-2 text-[11px] font-mono break-all mb-3">
        {regLink}
      </div>
      <ShareButtons url={regLink} emailSubject="100 € Guthaben bei DP Connect" qrFilename={`dp-registrierung-${def.invite_code}`} />

      <div className="mt-3 pt-3 border-t border-emerald-200/60 text-xs text-ink-muted">
        <strong className="text-ink">Ohne Link?</strong> Dein Code kann bei
        der Registrierung auch manuell eingetragen werden:{" "}
        <code className="font-mono font-semibold text-ink bg-white border border-line rounded px-1.5 py-0.5">
          {def.invite_code}
        </code>
        <button
          type="button"
          onClick={copyCode}
          className="ml-1.5 inline-flex items-center align-middle text-ink-muted hover:text-ink"
          title="Code kopieren"
        >
          {copiedCode ? (
            <Check className="size-3.5 text-emerald-700" />
          ) : (
            <Copy className="size-3.5" />
          )}
        </button>{" "}
        — der Code ist alles nach <code className="font-mono">{paramName}=</code> im Link.
      </div>
    </Card>
  );
}

// ============================================================================
// Shop-Modus — Floating-Button auf dpconnect.de
// ============================================================================

function ShopModeCard() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function activate() {
    setLoading(true);
    setErr(null);
    try {
      const res = await getShopModeLink();
      window.location.href = res.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Fehler");
      setLoading(false);
    }
  }

  return (
    <Card className="mb-6 brand-gradient-soft border-accent-orange/40">
      <div className="flex items-start gap-3 mb-3">
        <ShoppingBag className="size-5 text-accent-orange flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            Shop-Modus aktivieren
            <InfoTip title="Wie funktioniert das technisch?">
              Wir öffnen dpconnect.de mit einem speziellen Cookie, der dich
              als angemeldeten Agent markiert. Auf jeder Seite siehst du
              unten rechts einen schwebenden Button — Klick → fertiger
              Teilen-Link für genau das Produkt das du gerade anschaust,
              automatisch mit deinem Code.
              <br />
              <br />
              Der Modus läuft 24h, dann musst du ihn neu starten.
            </InfoTip>
          </h3>
          <p className="mt-1 text-xs text-ink-soft leading-relaxed">
            Surf direkt auf <code className="font-mono">dpconnect.de</code> — bei jeder Seite
            erscheint unten rechts ein Teilen-Button mit deinem Empfehlungs-Code. Du musst
            nicht mehr hier zurück und Produkte suchen.
          </p>
        </div>
      </div>
      {err && (
        <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {err}
        </div>
      )}
      <Button onClick={activate} disabled={loading}>
        <ShoppingBag className="size-4" />
        {loading ? "Öffne dpconnect.de…" : "Shop-Modus starten"}
      </Button>
    </Card>
  );
}

// ============================================================================
// Produkt-Link-Generator (Hauptfeature)
// ============================================================================

function ProductLinkGenerator() {
  const userIsAgent = isAgent();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [page, setPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<WCProduct | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedTerm(searchTerm), 400);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedTerm]);

  const { data, isFetching, error } = useQuery<WCProductSearchResponse>({
    queryKey: ["agent-products", debouncedTerm, page],
    queryFn: () =>
      searchProducts({
        search: debouncedTerm || undefined,
        page,
        perPage: 12,
      }),
    enabled: userIsAgent && debouncedTerm.length >= 2,
  });

  const displayItems = data?.items || [];

  return (
    <Card className="mb-8">
      <div className="mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Link2 className="size-4 text-accent-orange" />
          Link zu einem bestimmten Produkt
          <InfoTip title="Wann nutze ich das?">
            Für gezielte Empfehlungen — du weißt was der Kunde will und
            schickst ihm direkt den Produkt-Link. Der Kunde landet direkt
            auf der Produkt-Seite (statt erst die Suche durchwühlen zu
            müssen) — Konversion deutlich höher.
            <br />
            <br />
            <strong>VAR-Badge:</strong> Variable Produkte (z.B. mit
            Geschmacksrichtungen) → Kunde wählt Variante selbst.
          </InfoTip>
        </h3>
        <p className="mt-1 text-xs text-ink-muted">
          Suche ein Produkt aus dem Shop, wähle deinen Code, fertig. Variable Produkte führen zur
          Auswahl-Seite — der Kunde wählt dort die Variante selbst.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-muted" />
        <Input
          type="text"
          placeholder="z.B. 'Snackbox Premium', 'Mars' oder Produkt-ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          disabled={!userIsAgent}
        />
      </div>

      {data && (
        <div className="mt-3 text-xs text-ink-muted text-right">
          {displayItems.length} auf Seite {page}
          {data.total_pages > 1 && ` von ${data.total_pages}`}
        </div>
      )}

      {debouncedTerm.length < 2 && (
        <p className="mt-3 text-xs text-ink-muted italic">Mindestens 2 Zeichen tippen…</p>
      )}

      {isFetching && (
        <p className="mt-3 text-xs text-ink-muted">Suche im Shop…</p>
      )}

      {error && (
        <p className="mt-3 text-xs text-rose-700">
          {error instanceof Error ? error.message : "Fehler bei der Suche"}
        </p>
      )}

      {data && displayItems.length === 0 && !isFetching && debouncedTerm.length >= 2 && (
        <p className="mt-3 text-xs text-ink-muted">Keine Produkte gefunden.</p>
      )}

      {displayItems.length > 0 && (
        <>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {displayItems.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedProduct(p)}
                className={`text-left rounded-xl border bg-white p-3 hover:shadow-sm transition flex gap-3 relative ${
                  !p.purchasable || p.stock_status === "outofstock"
                    ? "border-rose-200 opacity-75 hover:border-rose-300"
                    : "border-line hover:border-accent-orange"
                }`}
              >
                {p.type === "variable" && (
                  <span className="absolute right-2 top-2 rounded-full bg-sky-100 text-sky-900 text-[9px] font-medium px-1.5 py-0.5">
                    VAR
                  </span>
                )}
                {p.image_src ? (
                  <img
                    src={p.image_src}
                    alt=""
                    className="size-14 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="size-14 rounded-lg bg-cream-soft flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-medium leading-snug line-clamp-2">{p.name}</h4>
                  <div className="mt-1 text-xs font-mono text-ink-muted">#{p.id}</div>
                  {p.price && (
                    <div className="text-xs font-semibold text-accent-orange mt-0.5">
                      {p.price} €
                    </div>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    <StockBadge
                      stockStatus={p.stock_status}
                      purchasable={p.purchasable}
                      stockQuantity={p.stock_quantity}
                      compact
                    />
                    {p.estimated_earning_cents != null && p.estimated_earning_cents > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800"
                        title={
                          p.estimated_earning_basis === "margin"
                            ? "Dein geschätzter Verdienst — basiert auf der Marge dieses Produkts."
                            : "Dein geschätzter Verdienst pro Verkauf."
                        }
                      >
                        + {(p.estimated_earning_cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {data && data.total_pages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="size-4" />
                Zurück
              </Button>
              <span className="text-xs text-ink-muted">
                Seite {page} von {data.total_pages} ({data.total} Produkte)
              </span>
              <Button
                variant="ghost"
                disabled={page >= data.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Weiter
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {selectedProduct && (
        <LinkGeneratorModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </Card>
  );
}

function LinkGeneratorModal({
  product,
  onClose,
}: {
  product: WCProduct;
  onClose: () => void;
}) {
  const { data: invites } = useQuery<AgentInvite[]>({
    queryKey: ["agent-invites"],
    queryFn: () => listMyInvites(),
  });
  const activeInvites = invites?.filter((i) => i.is_active) || [];
  const [selectedCode, setSelectedCode] = useState<string>("");

  useEffect(() => {
    if (!selectedCode && activeInvites.length > 0) {
      const def = activeInvites.find((i) => i.is_default) || activeInvites[0];
      setSelectedCode(def.invite_code);
    }
  }, [activeInvites, selectedCode]);

  const generate = useMutation({
    mutationFn: () => generateProductLink(product.id, selectedCode),
  });

  // Trigger generation when code changes
  useEffect(() => {
    if (selectedCode) {
      generate.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCode]);

  const url = generate.data?.url;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-cream rounded-2xl border border-line p-5 max-w-xl w-full shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          {product.image_src && (
            <img
              src={product.image_src}
              alt=""
              className="size-16 object-cover rounded-lg flex-shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-base leading-tight">{product.name}</h3>
            <div className="text-xs text-ink-muted font-mono mt-1">Produkt #{product.id}</div>
            {product.price && (
              <div className="text-sm text-accent-orange font-medium mt-1">{product.price} €</div>
            )}
            <div className="mt-2">
              <StockBadge
                stockStatus={product.stock_status}
                purchasable={product.purchasable}
                stockQuantity={product.stock_quantity}
              />
            </div>
            {product.estimated_earning_cents != null && product.estimated_earning_cents > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-sm font-semibold text-emerald-800">
                <span>💰</span>
                <span>
                  Dein Verdienst pro Verkauf:{" "}
                  {(product.estimated_earning_cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2">
            Mit welchem Code?
          </label>
          <select
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
            className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm"
          >
            {activeInvites.map((i) => (
              <option key={i.id} value={i.invite_code}>
                {i.invite_code}
                {i.is_default ? " (Default)" : ""}
                {i.label ? ` — ${i.label}` : ""}
              </option>
            ))}
          </select>
        </div>

        {(product.stock_status === "outofstock" || !product.purchasable) && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900">
            <strong>⚠️ Achtung:</strong> Dieses Produkt ist aktuell{" "}
            {!product.purchasable ? "nicht kaufbar" : "ausverkauft"}. Wenn ein
            Kunde deinen Link klickt, sieht er die Shop-Seite mit dem Hinweis
            „nicht verfügbar". Du kannst trotzdem einen Link erstellen, aber er
            wird in dem Status nicht konvertieren.
          </div>
        )}
        {product.stock_status === "onbackorder" && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            ℹ️ Lieferbar auf Nachbestellung — Kunden können kaufen, aber Versand
            dauert länger als üblich.
          </div>
        )}

        <div className="mb-4">
          <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2">
            Dein Link
          </label>
          {generate.isPending && (
            <div className="rounded-xl border border-line bg-cream-soft px-3 py-3 text-xs text-ink-muted">
              Generiere…
            </div>
          )}
          {generate.error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {generate.error instanceof Error ? generate.error.message : "Fehler"}
            </div>
          )}
          {url && (
            <div className="rounded-xl border border-line bg-white px-3 py-3 text-xs font-mono break-all">
              {url}
            </div>
          )}
        </div>

        {url && (
          <div className="mb-3">
            <ShareButtons
              url={url}
              emailSubject={`Empfehlung: ${product.name}`}
            />
          </div>
        )}

        <div className="flex gap-2">
          {url && (
            <Button variant="secondary" onClick={() => window.open(url, "_blank")}>
              <ExternalLink className="size-4" />
              Vorschau
            </Button>
          )}
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Invite-Codes (Default + Campaign)
// ============================================================================

function InvitesSection() {
  const userIsAgent = isAgent();
  const queryClient = useQueryClient();
  const { data: invites, isLoading } = useQuery<AgentInvite[]>({
    queryKey: ["agent-invites"],
    queryFn: () => listMyInvites(),
    enabled: userIsAgent,
  });
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newSuffix, setNewSuffix] = useState("");

  const create = useMutation({
    mutationFn: () => createInvite(newLabel.trim(), newSuffix.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-invites"] });
      setNewLabel("");
      setNewSuffix("");
      setShowNew(false);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-invites"] }),
  });

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            Meine Empfehlungs-Codes
            <InfoTip title="Warum mehrere Codes?">
              Jeder Code zählt eigene Klicks + Anmeldungen. Mit{" "}
              <strong>Kampagnen-Codes</strong> siehst du z.B. ob die
              Instagram-Story (Code <code>DEIN_CODE-insta</code>) besser
              läuft als der Newsletter (Code <code>DEIN_CODE-newsletter</code>)
              — und kannst dein Marketing-Budget gezielt einsetzen.
              <br />
              <br />
              Der <em>Default-Code</em> ist dein Standard ohne Suffix — wird
              überall verwendet wo du keinen spezifischen wählst.
            </InfoTip>
          </h3>
          <p className="text-xs text-ink-muted mt-0.5">
            Jeder Code ist ein eigener Tracking-Link. Mehrere für unterschiedliche Kampagnen.
          </p>
        </div>
        <Button onClick={() => setShowNew((s) => !s)} variant="secondary">
          <Plus className="size-4" />
          Neuer Code
        </Button>
      </div>

      {showNew && (
        <div className="rounded-xl border border-line bg-cream-soft/40 p-3 mb-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">
              Bezeichnung (für dich) *
            </label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="z.B. Instagram-Kampagne April"
            />
            <FieldHelp>
              Nur für dich sichtbar — hilft dir, deine Codes auseinanderzuhalten.
            </FieldHelp>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">
              Code-Suffix (optional)
            </label>
            <div className="flex gap-2">
              <Input
                value={newSuffix}
                onChange={(e) => setNewSuffix(e.target.value)}
                placeholder="z.B. 'insta-april' — leer = aus Label generiert"
                pattern="[A-Za-z0-9_-]+"
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={async () => {
                  try {
                    const res = await generateInviteSuffix(newLabel);
                    setNewSuffix(res.suffix);
                  } catch {
                    // silent
                  }
                }}
                title="Automatisch ein einzigartiges Suffix generieren"
              >
                <Dices className="size-4" />
                Generieren
              </Button>
            </div>
            <p className="mt-1 text-xs text-ink-muted">
              Resultierender Code: <code className="font-mono">DEIN_CODE-{newSuffix || "..."}</code>
              {!newSuffix && " — wird beim Speichern aus dem Label generiert (z.B. 'instagram-kampagne-april')"}
            </p>
          </div>
          {create.error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {create.error instanceof Error ? create.error.message : "Fehler"}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowNew(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!newLabel.trim() || create.isPending}
            >
              {create.isPending ? "Anlegen…" : "Code anlegen"}
            </Button>
          </div>
        </div>
      )}

      {isLoading && <div className="text-sm text-ink-muted">Lade…</div>}

      <div className="space-y-2">
        {invites?.map((invite) => (
          <InviteRow
            key={invite.id}
            invite={invite}
            onDelete={() => remove.mutate(invite.id)}
          />
        ))}
      </div>
    </Card>
  );
}

function InviteRow({ invite, onDelete }: { invite: AgentInvite; onDelete: () => void }) {
  return (
    <div
      className={cn(
        "rounded-xl border bg-white p-3 space-y-2",
        invite.is_active ? "border-line" : "border-line/50 opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <code className="font-mono text-sm font-semibold">{invite.invite_code}</code>
            {invite.is_default && (
              <span className="rounded-full bg-accent-orange/10 text-accent-orange text-[10px] font-medium px-2 py-0.5">
                DEFAULT
              </span>
            )}
            {!invite.is_active && (
              <span className="rounded-full bg-slate-200 text-slate-700 text-[10px] font-medium px-2 py-0.5">
                INAKTIV
              </span>
            )}
          </div>
          {invite.label && (
            <div className="text-xs text-ink-muted mb-1">{invite.label}</div>
          )}
          <div className="text-[11px] font-mono text-ink-muted truncate">{invite.ref_link}</div>
          <div className="mt-1 text-[11px] text-ink-muted">
            {invite.clicks_count} Klicks · {invite.signups_count} Anmeldungen
          </div>
        </div>
        {!invite.is_default && invite.is_active && (
          <button
            onClick={onDelete}
            className="text-xs text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-lg inline-flex items-center gap-1 flex-shrink-0"
            title="Code deaktivieren"
          >
            <Trash2 className="size-3" />
            <span className="hidden sm:inline">Deaktivieren</span>
          </button>
        )}
      </div>

      {invite.is_active && (
        <ShareButtons
          url={invite.ref_link}
          emailSubject="Empfehlung von DP Connect"
          qrFilename={`dp-link-${invite.invite_code}`}
          compact
        />
      )}
    </div>
  );
}
